import React, { useState } from 'react';
import {
    Calendar,
    Clock,
    Users,
    RotateCcw,
    Check,
    Edit,
    AlertCircle
} from 'lucide-react';

const SchedulingDemo = () => {
    // 模擬排班數據
    const [scheduleData, setScheduleData] = useState({
        departments: [
            {
                id: 1,
                name: '銷售部門',
                staffCount: 12,
                shiftTypes: ['早班', '晚班', '中班']
            },
            {
                id: 2,
                name: '客服部門',
                staffCount: 8,
                shiftTypes: ['早班', '晚班']
            },
            {
                id: 3,
                name: '技術研發部',
                staffCount: 6,
                shiftTypes: ['彈性工作']
            }
        ],
        currentWeekSchedule: [
            {
                date: '2024-03-11',
                day: '一',
                shifts: [
                    { department: '銷售部門', type: '早班', staffAllocated: 6, staffRequired: 8 },
                    { department: '客服部門', type: '晚班', staffAllocated: 4, staffRequired: 5 }
                ]
            },
            {
                date: '2024-03-12',
                day: '二',
                shifts: [
                    { department: '銷售部門', type: '晚班', staffAllocated: 7, staffRequired: 8 },
                    { department: '技術研發部', type: '彈性工作', staffAllocated: 6, staffRequired: 6 }
                ]
            }
            // 其他日期的排班數據
        ],
        employeeSchedule: [
            {
                name: '王小明',
                department: '銷售部門',
                shifts: [
                    { date: '2024-03-11', type: '早班', startTime: '08:00', endTime: '16:00' },
                    { date: '2024-03-12', type: '晚班', startTime: '16:00', endTime: '00:00' }
                ]
            },
            {
                name: '李小華',
                department: '客服部門',
                shifts: [
                    { date: '2024-03-11', type: '晚班', startTime: '16:00', endTime: '00:00' },
                    { date: '2024-03-12', type: '早班', startTime: '08:00', endTime: '16:00' }
                ]
            }
        ]
    });

    // 處理智能排班
    const handleSmartScheduling = () => {
        // 模擬智能排班邏輯
        const updatedSchedule = { ...scheduleData };
        // 在此添加排班演算法邏輯
        setScheduleData(updatedSchedule);
    };

    return (
        <div className="p-8">
            <div className="grid md:grid-cols-2 gap-8">
                {/* 左側：部門排班概況 */}
                <div>
                    <h2 className="text-2xl font-bold mb-4 flex items-center">
                        <Calendar className="mr-2 text-primary-600" />
                        部門排班管理
                    </h2>

                    <div className="space-y-4">
                        {scheduleData.departments.map((dept) => (
                            <div
                                key={dept.id}
                                className="bg-white p-4 rounded-lg shadow-sm"
                            >
                                <div className="flex justify-between items-center mb-3">
                                    <h3 className="font-semibold">{dept.name}</h3>
                                    <div className="flex items-center">
                                        <Users className="mr-2 text-primary-600" />
                                        <span>{dept.staffCount}人</span>
                                    </div>
                                </div>

                                <div className="flex space-x-2">
                                    {dept.shiftTypes.map((shiftType) => (
                                        <span
                                            key={shiftType}
                                            className="px-2 py-1 bg-primary-50 text-primary-600 rounded-full text-sm"
                                        >
                                            {shiftType}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                        <h3 className="font-semibold mb-3 flex items-center">
                            <AlertCircle className="mr-2 text-yellow-500" />
                            排班注意事項
                        </h3>
                        <ul className="list-disc list-inside text-sm text-gray-600 space-y-2">
                            <li>確保每個班次人力充足</li>
                            <li>遵守勞動法規的工時限制</li>
                            <li>考慮員工個人休息與輪班需求</li>
                            <li>平衡技能分佈與工作負載</li>
                        </ul>
                    </div>
                </div>

                {/* 右側：本週排班詳情 */}
                <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center">
                        <Clock className="mr-2 text-primary-600" />
                        本週排班詳情
                    </h3>

                    <div className="bg-white rounded-lg shadow-md overflow-hidden">
                        <div className="p-4 bg-gray-100 flex justify-between items-center">
                            <span className="font-semibold">2024年 第11週</span>
                            <div className="flex space-x-2">
                                <button
                                    onClick={handleSmartScheduling}
                                    className="bg-primary-600 text-white px-3 py-1 rounded-lg text-sm flex items-center hover:bg-primary-700 transition-colors"
                                >
                                    <RotateCcw className="mr-1" size={16} />
                                    智能重新排班
                                </button>
                                <button
                                    className="border border-primary-600 text-primary-600 px-3 py-1 rounded-lg text-sm flex items-center hover:bg-primary-50 transition-colors"
                                >
                                    <Edit className="mr-1" size={16} />
                                    手動調整
                                </button>
                            </div>
                        </div>

                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="p-3 text-left">日期</th>
                                    <th className="p-3 text-left">部門</th>
                                    <th className="p-3 text-center">班次</th>
                                    <th className="p-3 text-center">人力配置</th>
                                </tr>
                            </thead>
                            <tbody>
                                {scheduleData.currentWeekSchedule.flatMap((day) =>
                                    day.shifts.map((shift, index) => (
                                        <tr key={`${day.date}-${index}`} className="border-b">
                                            <td className="p-3">
                                                {day.date} ({day.day})
                                            </td>
                                            <td className="p-3">{shift.department}</td>
                                            <td className="p-3 text-center">{shift.type}</td>
                                            <td className="p-3 text-center">
                                                <div className="flex items-center justify-center">
                                                    <span className={`font-semibold ${shift.staffAllocated < shift.staffRequired
                                                            ? 'text-red-600'
                                                            : 'text-green-600'
                                                        }`}>
                                                        {shift.staffAllocated}/{shift.staffRequired}
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold mb-3 flex items-center">
                            <Check className="mr-2 text-green-500" />
                            智能排班特點
                        </h4>
                        <p className="text-sm text-gray-600">
                            系統根據部門人力需求、員工技能、工時限制等因素，
                            自動生成最優排班方案。可靈活調整，平衡工作負載，
                            提高員工滿意度和工作效率。
                        </p>
                    </div>
                </div>
            </div>

            {/* 底部操作區域 */}
            <div className="mt-8 flex justify-between items-center bg-white p-6 rounded-lg shadow-md">
                <div>
                    <h4 className="font-semibold">排班管理與優化</h4>
                    <p className="text-sm text-gray-600">
                        持續優化排班策略，平衡員工需求與企業運營效率
                    </p>
                </div>
                <div className="flex space-x-4">
                    <button className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors">
                        排班策略設定
                    </button>
                    <button className="border border-primary-600 text-primary-600 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors">
                        歷史數據分析
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SchedulingDemo;